import { useEffect, useState } from "react";
import { CheckCircle2, FileSignature, FileText, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { fmtDateTime, fmtEuro } from "@/lib/format";
import { Btn, Pill, ProgressBar } from "@/components/rp";
import { SignaturePad } from "@/components/SignaturePad";

const TONE = { da_fare: "muted", inviata: "warning", completata: "success" };
const CLIENT_LABEL = { da_fare: "In preparazione", inviata: "Tocca a te", completata: "Fatto" };

const SignForm = ({ onSign, testid }) => {
  const [ok, setOk] = useState(false);
  const [name, setName] = useState("");
  return (
    <div className="mt-3 space-y-2">
      <label className="flex gap-2 text-sm items-start cursor-pointer"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-1 h-4 w-4 accent-black" data-testid={`${testid}-checkbox`} />Dichiaro di aver letto il documento e di accettarlo.</label>
      <div className="flex gap-2"><input value={name} onChange={(e) => setName(e.target.value)} className="rp-field bg-white" placeholder="Nome e cognome (firma)" data-testid={`${testid}-name`} />
        <Btn disabled={!ok || name.trim().length < 3} onClick={() => onSign(name)} data-testid={`${testid}-button`}><FileSignature className="h-4 w-4" /> Firma</Btn></div>
    </div>
  );
};

const Action = ({ it, base, onDone }) => {
  const [text, setText] = useState("");
  const run = async (fn) => { try { onDone((await fn()).data); toast.success("Fatto, grazie!"); } catch (e) { toast.error(errMsg(e)); } };
  const upload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    run(() => api.post(`${base}/file`, fd));
  };
  const tid = `pratica-${it.key}`;
  if (it.kind === "richiesta_file")
    return <label className="rp-btn bg-[#070707] text-white cursor-pointer mt-3 h-10" data-testid={`${tid}-upload`}><Upload className="h-4 w-4" /> Carica foto o PDF<input type="file" accept=".pdf,image/*" className="hidden" onChange={upload} /></label>;
  if (it.kind === "richiesta_testo")
    return (
      <div className="flex gap-2 mt-3"><input value={text} onChange={(e) => setText(e.target.value)} className="rp-field bg-white" placeholder={it.key === "codice_fiscale" ? "Codice fiscale (16 caratteri)" : "Via, numero, CAP, città"} data-testid={`${tid}-input`} />
        <Btn disabled={text.trim().length < 5} onClick={() => run(() => api.post(`${base}/text`, { value: text }))} data-testid={`${tid}-send`}>Invia</Btn></div>
    );
  if (it.sign)
    return (
      <div className="grid md:grid-cols-2 gap-4 mt-3">
        <div className="rounded-xl bg-white p-3"><p className="text-xs font-semibold mb-1">Firma online</p><SignForm testid={`${tid}-sign`} onSign={(name) => run(() => api.post(`${base}/sign`, { name, accept: true }))} /></div>
        <div className="rounded-xl bg-white p-3"><p className="text-xs font-semibold mb-2">Oppure scarica, firma e ricarica</p>
          <div className="flex flex-wrap gap-2">
            <a href={fileUrl(`/api${base}/file?which=doc`)} target="_blank" rel="noreferrer" className="rp-btn bg-white border border-[#EBEBEB] h-10" data-testid={`${tid}-download`}><Download className="h-4 w-4" /> Scarica il PDF</a>
            <label className="rp-btn bg-[#070707] text-white cursor-pointer h-10" data-testid={`${tid}-signed-upload`}><Upload className="h-4 w-4" /> Carica il documento firmato
              <input type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (!f) return; const fd = new FormData(); fd.append("file", f); run(() => api.post(`${base}/signed-upload`, fd)); }} /></label>
          </div></div>
      </div>
    );
  return null;
};

const ExtraAccept = ({ x, oid, onDone }) => {
  const [ok, setOk] = useState(false);
  const [name, setName] = useState("");
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (path, body) => {
    setBusy(true);
    try { onDone((await api.post(`/client/orders/${oid}/extras/${x.id}/${path}`, body)).data); toast.success(path === "accept" ? "Extra accettato, grazie!" : "Risposta inviata"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="mt-3 space-y-3">
      <SignaturePad onChange={setSig} testid={`extra-signature-${x.id}`} />
      <label className="flex gap-2 text-sm items-start cursor-pointer"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-1 h-4 w-4 accent-black" data-testid={`extra-accept-checkbox-${x.id}`} />Ho letto il documento di spesa e accetto l'extra e il relativo importo.</label>
      <div className="flex flex-wrap gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} className="rp-field bg-white flex-1 min-w-[200px]" placeholder="Nome e cognome" data-testid={`extra-accept-name-${x.id}`} />
        <Btn loading={busy} disabled={!ok || !sig || name.trim().length < 3} onClick={() => run("accept", { name, accept: true, signature: sig })} data-testid={`extra-accept-button-${x.id}`}><FileSignature className="h-4 w-4" /> Accetto e firmo</Btn>
        <Btn variant="outline" disabled={busy} onClick={() => { const note = window.prompt("Motivo del rifiuto (facoltativo)") ; if (note !== null) run("reject", { value: note }); }} data-testid={`extra-reject-button-${x.id}`}>Rifiuta</Btn>
      </div>
    </div>
  );
};

const Extras = ({ extras, oid, onDone }) => (
  <div className="mt-6" data-testid="client-extras">
    <p className="rp-label mb-2">Extra</p>
    <div className="space-y-2">
      {extras.map((x) => (
        <div key={x.id} className={`rounded-2xl border p-4 ${x.status === "inviato" ? "bg-[#E6DDCE]/40 border-[#C8BDAB]" : "bg-white border-[#EBEBEB]"}`} data-testid={`client-extra-${x.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium flex-1 min-w-[160px]">Extra n. {x.number} · {x.title}</span>
            <span className="text-sm font-semibold">{fmtEuro(x.amount)}</span>
            <Pill tone={x.status === "accettato" ? "success" : x.status === "rifiutato" ? "error" : "warning"}>{x.status === "inviato" ? "Da approvare" : x.status_label}</Pill>
          </div>
          {x.description && <p className="text-sm text-[#757575] mt-1">{x.description}</p>}
          <div className="flex flex-wrap gap-3 mt-1 text-xs">
            {x.has_doc && <a href={fileUrl(`/api/client/orders/${oid}/extras/${x.id}/file?which=doc`)} target="_blank" rel="noreferrer" className="underline" data-testid={`client-extra-doc-${x.id}`}>Documento di spesa</a>}
            {x.has_signed && <a href={fileUrl(`/api/client/orders/${oid}/extras/${x.id}/file?which=signed`)} target="_blank" rel="noreferrer" className="underline">Accettazione firmata</a>}
            {x.accepted_at && <span className="text-[#757575]">Accettato il {fmtDateTime(x.accepted_at)}</span>}
          </div>
          {x.status === "inviato" && <ExtraAccept x={x} oid={oid} onDone={onDone} />}
        </div>
      ))}
    </div>
  </div>
);

const VariantAccept = ({ v, oid, onDone }) => {
  const [ok, setOk] = useState(false);
  const [name, setName] = useState("");
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (path, body) => {
    setBusy(true);
    try { onDone((await api.post(`/client/orders/${oid}/varianti/${v.id}/${path}`, body)).data); toast.success(path === "accept" ? "Variante approvata, grazie!" : "Risposta inviata"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="mt-3 space-y-3">
      <SignaturePad onChange={setSig} testid={`variant-signature-${v.id}`} />
      <label className="flex gap-2 text-sm items-start cursor-pointer"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-1 h-4 w-4 accent-black" data-testid={`variant-accept-checkbox-${v.id}`} />Ho letto il computo lavori e approvo la variante e il relativo importo.</label>
      <div className="flex flex-wrap gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} className="rp-field bg-white flex-1 min-w-[200px]" placeholder="Nome e cognome" data-testid={`variant-accept-name-${v.id}`} />
        <Btn loading={busy} disabled={!ok || !sig || name.trim().length < 3} onClick={() => run("accept", { name, accept: true, signature: sig })} data-testid={`variant-accept-button-${v.id}`}><FileSignature className="h-4 w-4" /> Approvo e firmo</Btn>
        <Btn variant="outline" disabled={busy} onClick={() => { const note = window.prompt("Motivo del rifiuto (facoltativo)"); if (note !== null) run("reject", { value: note }); }} data-testid={`variant-reject-button-${v.id}`}>Rifiuta</Btn>
      </div>
    </div>
  );
};

const VTONE = { inviata: "warning", accettata: "success", rifiutata: "error", superata: "muted" };

const Variants = ({ oid }) => {
  const [list, setList] = useState([]);
  useEffect(() => { api.get(`/client/orders/${oid}/varianti`).then((r) => setList(r.data)).catch(() => {}); }, [oid]);
  if (!list.length) return null;
  return (
    <div className="mt-6" data-testid="client-variants">
      <p className="rp-label mb-2">Varianti del computo</p>
      <div className="space-y-2">
        {list.map((v) => (
          <div key={v.id} className={`rounded-2xl border p-4 ${v.status === "inviata" ? "bg-[#E6DDCE]/40 border-[#C8BDAB]" : "bg-white border-[#EBEBEB]"}`} data-testid={`client-variant-${v.variant}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium flex-1 min-w-[160px]">Computo lavori · variante {v.variant}</span>
              {v.total != null && <span className="text-sm font-semibold">{fmtEuro(v.total)} IVA incl.</span>}
              <Pill tone={VTONE[v.status]}>{v.status_label}</Pill>
            </div>
            <div className="flex flex-wrap gap-3 mt-1 text-xs">
              <a href={fileUrl(`/api/orders/${oid}/documents/${v.id}/file`)} target="_blank" rel="noreferrer" className="underline" data-testid={`client-variant-doc-${v.variant}`}>Apri il computo</a>
              {v.signed_id && <a href={fileUrl(`/api/orders/${oid}/documents/${v.signed_id}/file`)} target="_blank" rel="noreferrer" className="underline">Approvazione firmata</a>}
              {v.accepted_at && <span className="text-[#757575]">Approvata il {fmtDateTime(v.accepted_at)}</span>}
            </div>
            {v.status === "inviata" && <VariantAccept v={v} oid={oid} onDone={setList} />}
          </div>
        ))}
      </div>
    </div>
  );
};

const Item = ({ it, oid, onDone }) => {
  const base = `/client/orders/${oid}/pratica/${it.key}`;
  return (
    <div className={`rounded-2xl border p-4 ${it.status === "inviata" ? "bg-[#E6DDCE]/40 border-[#C8BDAB]" : "bg-white border-[#EBEBEB]"}`} data-testid={`client-pratica-item-${it.key}`}>
      <div className="flex flex-wrap items-center gap-2">
        {it.status === "completata" ? <CheckCircle2 className="h-4 w-4 text-[#198754]" /> : <FileText className="h-4 w-4 text-[#757575]" />}
        <span className="text-sm font-medium flex-1 min-w-[160px] first-letter:uppercase">{it.label}</span>
        {it.amount != null && <span className="text-sm">{fmtEuro(it.amount)}</span>}
        <Pill tone={TONE[it.status]}>{CLIENT_LABEL[it.status]}</Pill>
      </div>
      <div className="flex flex-wrap gap-3 mt-1 text-xs text-[#757575]">
        {it.has_doc && <a href={fileUrl(`/api${base}/file?which=doc`)} target="_blank" rel="noreferrer" className="underline text-black" data-testid={`client-pratica-doc-${it.key}`}>Apri il documento</a>}
        {it.has_signed && <a href={fileUrl(`/api${base}/file?which=signed`)} target="_blank" rel="noreferrer" className="underline text-black">Copia firmata</a>}
        {it.value && <span>Inserito: {it.value}</span>}
        {it.done_at && <span>Completato il {fmtDateTime(it.done_at)}</span>}
      </div>
      {it.status === "inviata" && <Action it={it} base={base} onDone={onDone} />}
    </div>
  );
};

export const ClientPratica = ({ oid }) => {
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/client/orders/${oid}/pratica`).then((r) => setP(r.data)).catch(() => {}); }, [oid]);
  if (!p) return null;
  const todo = p.items.filter((i) => i.status === "inviata").length + (p.extras || []).filter((x) => x.status === "inviato").length;
  return (
    <div className="mt-6 pt-5 border-t border-[#EBEBEB]" data-testid="client-pratica">
      <div className="flex items-center justify-between mb-2"><p className="rp-label">Documenti e fatture</p>{todo > 0 && <Pill tone="warning" data-testid="client-pratica-todo">{todo} da completare</Pill>}</div>
      <div className="flex justify-between text-xs mb-2"><span className="text-[#757575]">Pratica amministrativa</span><span className="font-semibold">{p.progress.done} di {p.progress.total}</span></div>
      <ProgressBar value={p.progress.total ? (p.progress.done / p.progress.total) * 100 : 0} />
      <div className="space-y-2 mt-4">{[...p.items].sort((a, b) => (a.status === "inviata" ? -1 : 0) - (b.status === "inviata" ? -1 : 0)).map((it) => <Item key={it.key} it={it} oid={oid} onDone={setP} />)}</div>
      {p.extras?.length > 0 && <Extras extras={p.extras} oid={oid} onDone={setP} />}
      <Variants oid={oid} />
    </div>
  );
};
